import {
  ButtonMediumPrimaryLeadingIcon,
  Icon,
} from "@studio3t/generic-components";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ConfirmDeleteModal } from "@/components/ConfirmDeleteModal";
import { EmptyState } from "@/components/EmptyState";
import { BreadcrumbCurrent, ListPageHeader } from "@/components/ListPageHeader";
import {
  queryPhase,
  useJobs,
  useRemoveService,
  useServices,
} from "@/hooks/useApi";
import type { Service } from "@/types/api";
import { providerRank } from "./helpers";
import { ServicesTable, type ServiceUsage } from "./ServicesTable";
import { PageRoot } from "./styles";
import { useServiceData } from "./useServiceData";
import { useSetDefaultLlm } from "./useSetDefaultLlm";

// Groups render in this order; within a group, services sort by name. Sorting
// the flat data up front sets both the group order (TanStack groups by first
// appearance) and the leaf order, since the list itself isn't sortable.
function sortServices(services: Service[]): Service[] {
  return [...services].sort((a, b) => {
    const order = providerRank(a.provider) - providerRank(b.provider);
    return order !== 0 ? order : a.name.localeCompare(b.name);
  });
}

// Delete flow: a request opens the confirmation modal; confirm performs the API
// call and keeps the modal open with an error message on failure.
function useDeleteService(removeService: (name: string) => Promise<unknown>) {
  const [pendingName, setPendingName] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const close = useCallback(() => {
    setPendingName(null);
    setError(null);
  }, []);

  // useRemoveService invalidates the services query, so the list refreshes
  // without a manual refetch here.
  const confirm = useCallback(async () => {
    if (!pendingName) return;
    setError(null);
    try {
      await removeService(pendingName);
      close();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete service");
    }
  }, [pendingName, removeService, close]);

  return { pendingName, request: setPendingName, confirm, close, error };
}

// Loads services + jobs and derives the sorted list and per-service usage map the
// table renders. Kept out of the component so the data wrangling doesn't inflate
// the component's complexity.
function useServicesData() {
  const { data: services, isLoading: loading, error } = useServices();
  const { data: jobs, error: jobsError } = useJobs();
  const { getServiceUsage } = useServiceData(jobs ?? null);

  const sorted = useMemo(
    () => (services ? sortServices(services) : []),
    [services],
  );

  const usageByService = useMemo(() => {
    const map: Record<string, ServiceUsage[]> = {};
    for (const service of sorted) {
      map[service.name] = getServiceUsage(service);
    }
    return map;
  }, [sorted, getServiceUsage]);

  return {
    services,
    loading,
    error,
    sorted,
    usageByService,
    // Lifecycle of the jobs query behind the Status/Used-by columns — "error"
    // stops their skeletons from spinning forever if the query fails.
    usagePhase: queryPhase(jobs, jobsError),
  };
}

function Services() {
  const navigate = useNavigate();
  const { mutateAsync: removeService, isPending: deleting } =
    useRemoveService();
  const del = useDeleteService(removeService);
  const { setDefault: setDefaultLlm, isPending: settingDefault } =
    useSetDefaultLlm();

  const { services, loading, error, sorted, usageByService, usagePhase } =
    useServicesData();

  const handleRowClick = useCallback(
    (name: string) => {
      navigate(`/services/${encodeURIComponent(name)}`);
    },
    [navigate],
  );

  if (error) {
    return (
      <EmptyState
        icon={Icon.Warning}
        title="Failed to load services"
        description={error.message}
      />
    );
  }

  return (
    <PageRoot>
      <ListPageHeader
        title="Services"
        count={services?.length}
        breadcrumb={<BreadcrumbCurrent>Services list</BreadcrumbCurrent>}
        action={
          <ButtonMediumPrimaryLeadingIcon
            text="Add service"
            icon={Icon.Plus}
            onClick={() => navigate("/services/new")}
          />
        }
      />

      <ServicesTable
        services={sorted}
        usageByService={usageByService}
        // Used-by names derive from the jobs query; its phase drives the
        // Status/Used-by cell skeletons (and error dashes), and the table only
        // prunes deleted pipelines once the phase is "ready".
        usagePhase={usagePhase}
        // While the services query loads, the table shows skeleton rows.
        loading={loading && !services}
        onRowClick={handleRowClick}
        onDelete={del.request}
        onSetDefault={setDefaultLlm}
        settingDefault={settingDefault}
      />

      <ConfirmDeleteModal
        isOpen={del.pendingName !== null}
        title="Delete service"
        message={
          <>
            Are you sure you want to delete <strong>{del.pendingName}</strong>?
            This action cannot be undone.
          </>
        }
        loading={deleting}
        error={del.error}
        onConfirm={del.confirm}
        onCancel={del.close}
      />
    </PageRoot>
  );
}

export default Services;
